import { EXPERIMENT_STEPS, fieldValueError, type ExperimentStepKey } from '@dyeatlas/shared'
import { useMutation } from '@tanstack/react-query'
import { AlertTriangle, Check, ChevronLeft, ChevronRight, Cloud, CloudOff } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { isRecordEditable, type useRecordDraft } from '../useRecordDraft'
import { FieldControl } from './FieldControl'
import { PhotoUploader } from './PhotoUploader'
import { ReviewStep } from './ReviewStep'
import { STEP_FIELDS } from './fields'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/misc'
import { useMasterData } from '@/hooks/useMasterData'
import { api, ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'

type Draft = ReturnType<typeof useRecordDraft>

export function ExperimentWizard({ draft }: { draft: Draft }) {
  const navigate = useNavigate()
  const { options } = useMasterData()
  const [stepIndex, setStepIndex] = useState(0)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const step = EXPERIMENT_STEPS[stepIndex]
  const isReview = step.key === 'review'
  const { values, setField, completion, record } = draft
  const readOnly = !isRecordEditable(record)
  const isPublicCopy = Boolean(record?.sourceProjectId)
  const home = isPublicCopy ? '/database' : record?.projectId ? `/projects/${record.projectId}` : null

  const finish = useMutation({
    mutationFn: async () => {
      await draft.flush()
      return api.post<{ record: { id: string; code: string } }>(`/records/${record!.id}/finish`)
    },
    onSuccess: (result) => {
      toast.success(`Finished as ${result.record.code}.`)
      navigate(home ?? '/workspace/finished')
    },
    onError: (failure) => {
      if (failure instanceof ApiError) {
        setFieldErrors(failure.fieldErrors)
        toast.error(failure.message)
      }
    },
  })

  const goTo = (index: number) => {
    void draft.flush()
    setStepIndex(index)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="flex gap-7">
      <aside className="w-60 shrink-0">
        <ol className="space-y-0.5">
          {EXPERIMENT_STEPS.map((entry, index) => {
            const incomplete = completion.incompleteSteps.includes(entry.key)
            const visited = index < stepIndex
            return (
              <li key={entry.key}>
                <button
                  type="button"
                  onClick={() => goTo(index)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-[var(--radius-control)] px-3 py-2 text-left text-[13px] transition-colors',
                    index === stepIndex
                      ? 'bg-brand-soft font-semibold text-brand'
                      : 'text-ink-soft hover:bg-paper-muted hover:text-ink',
                  )}
                >
                  <StepMarker index={index} active={index === stepIndex} done={visited && !incomplete} warn={visited && incomplete} />
                  <span className="min-w-0 flex-1 truncate">{entry.title}</span>
                </button>
              </li>
            )
          })}
        </ol>

        <div className="mt-5 rounded-[var(--radius-control)] border border-line bg-paper p-3">
          <div className="flex items-baseline justify-between text-[12px]">
            <span className="font-semibold text-ink-soft">Completion</span>
            <span className="font-display text-base font-semibold text-ink">
              {completion.percent}%
            </span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-line">
            <div
              className="h-full rounded-full bg-brand transition-[width] duration-300"
              style={{ width: `${completion.percent}%` }}
            />
          </div>
          <p className="mt-2 text-[11px] leading-relaxed text-ink-muted">
            {completion.canFinish
              ? 'All required fields are filled in.'
              : `${completion.missingRequired.length} required field${completion.missingRequired.length === 1 ? '' : 's'} left.`}
          </p>
        </div>

        <SaveIndicator draft={draft} />
      </aside>

      <section className="min-w-0 flex-1">
        <Card className="p-6">
          <header className="mb-5">
            <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-faint">
              Step {stepIndex + 1} of {EXPERIMENT_STEPS.length}
            </p>
            <h2 className="mt-0.5 font-display text-2xl font-semibold text-ink">{step.title}</h2>
            <p className="mt-0.5 text-[13px] text-ink-muted">{step.description}</p>
          </header>

          {isReview ? (
            <ReviewStep
              values={values}
              completion={completion}
              options={options}
              onJumpToStep={(key: ExperimentStepKey) =>
                goTo(EXPERIMENT_STEPS.findIndex((entry) => entry.key === key))
              }
            />
          ) : (
            <fieldset disabled={readOnly} className="grid grid-cols-2 gap-x-5 gap-y-4">
              {STEP_FIELDS[step.key as keyof typeof STEP_FIELDS].map((field) => (
                <FieldControl
                  key={field.name}
                  field={field}
                  value={values[field.name]}
                  error={
                    fieldErrors[field.name] ?? fieldValueError('experiment', field.name, values[field.name])
                  }
                  options={options}
                  onChange={(value) => {
                    setFieldErrors((current) => {
                      if (!current[field.name]) return current
                      const { [field.name]: _removed, ...rest } = current
                      return rest
                    })
                    // A value the server would reject stays on screen, flagged, but is not autosaved.
                    if (fieldValueError('experiment', field.name, value)) {
                      draft.setValues((current) => ({ ...current, [field.name]: value }))
                    } else {
                      setField(field.name, value)
                    }
                  }}
                />
              ))}
            </fieldset>
          )}

          {step.key === 'results' && record && (
            <div className="mt-6 border-t border-line pt-5">
              <p className="field-label mb-2">Sample photos</p>
              <PhotoUploader
                recordId={record.id}
                photos={(values.photos as never) ?? []}
                disabled={readOnly}
                onChange={(photos) => draft.setValues((current) => ({ ...current, photos }))}
              />
            </div>
          )}
        </Card>

        <footer className="mt-4 flex items-center justify-between">
          <Button
            variant="secondary"
            disabled={stepIndex === 0}
            onClick={() => goTo(stepIndex - 1)}
          >
            <ChevronLeft className="size-4" />
            Back
          </Button>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() =>
                void draft.flush().then(() => navigate(home ?? '/workspace/drafts'))
              }
            >
              Save and close
            </Button>
            {isReview && isPublicCopy ? (
              <Button
                onClick={() =>
                  void draft.flush().then(() => {
                    toast.success('Public record saved.')
                    navigate('/database')
                  })
                }
              >
                <Check className="size-4" />
                Save public record
              </Button>
            ) : isReview ? (
              <Button
                loading={finish.isPending}
                disabled={!completion.canFinish || readOnly}
                onClick={() => finish.mutate()}
              >
                <Check className="size-4" />
                Finish experiment
              </Button>
            ) : (
              <Button onClick={() => goTo(stepIndex + 1)}>
                Next
                <ChevronRight className="size-4" />
              </Button>
            )}
          </div>
        </footer>
      </section>
    </div>
  )
}

function StepMarker({
  index,
  active,
  done,
  warn,
}: {
  index: number
  active: boolean
  done: boolean
  warn: boolean
}) {
  return (
    <span
      className={cn(
        'flex size-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold',
        active && 'border-brand bg-brand text-white',
        !active && done && 'border-positive bg-positive-soft text-positive',
        !active && warn && 'border-caution bg-caution-soft text-caution',
        !active && !done && !warn && 'border-line text-ink-faint',
      )}
    >
      {done ? <Check className="size-3" /> : warn ? <AlertTriangle className="size-3" /> : index + 1}
    </span>
  )
}

/** Autosave status, so the researcher never wonders whether work is safe. */
function SaveIndicator({ draft }: { draft: Draft }) {
  const { saveState, savedAt } = draft

  const label =
    saveState === 'saving'
      ? 'Saving…'
      : saveState === 'error'
        ? 'Could not save'
        : savedAt
          ? `Saved at ${savedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
          : 'Saved automatically'

  return (
    <p
      className={cn(
        'mt-3 flex items-center gap-1.5 px-1 text-[11px]',
        saveState === 'error' ? 'text-brand' : 'text-ink-faint',
      )}
    >
      {saveState === 'error' ? <CloudOff className="size-3.5" /> : <Cloud className="size-3.5" />}
      {label}
    </p>
  )
}
